import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AnalyticsReportParams,
  WorkExperienceOptionalSettings,
  WorkExperienceReportCustomParams,
} from '~/data/reports';

type SelectOptions = {
  value: WorkExperienceOptionalSettings | string;
  label: string;
};

export type CustomParamsConfig = {
  key: WorkExperienceOptionalSettings;
  name: string;
};

export const useWorkExperienceReportCustomParamsSelector = () => {
  const { t } = useTranslation();

  const {
    customParamsConfig,
    customParamSwitches,
    selectOptions,
    defaultOption,
  } = useMemo(() => {
    const customParamsConfig: CustomParamsConfig[] = [
      {
        key: WorkExperienceOptionalSettings.IncludeCompanyHistory,
        name: t('laHistoryCheck'),
      },
      {
        key: WorkExperienceOptionalSettings.UseOriginalStartDate,
        name: t('laEmpOrigStartDate'),
      },
      {
        key: WorkExperienceOptionalSettings.IncludeExternalExperienceAndLongAbsences,
        name: t('includePreviousCompanyAndAbsences'),
      },
      {
        key: WorkExperienceOptionalSettings.DeductLongAbsences,
        name: t('deductLongAbsences'),
      },
    ];

    const customParamSwitches = customParamsConfig.filter(({ key }) =>
      [
        WorkExperienceOptionalSettings.IncludeExternalExperienceAndLongAbsences,
        WorkExperienceOptionalSettings.DeductLongAbsences,
      ].includes(key),
    );

    const customParamsSelectOptions = customParamsConfig
      .filter(({ key }) =>
        [
          WorkExperienceOptionalSettings.IncludeCompanyHistory,
          WorkExperienceOptionalSettings.UseOriginalStartDate,
        ].includes(key),
      )
      .map(({ key, name }): SelectOptions => ({ label: name, value: key }));

    const defaultOption: SelectOptions = {
      label: t('fEmplDateStart'),
      value: 'default',
    };

    const selectOptions: SelectOptions[] = [
      defaultOption,
      ...customParamsSelectOptions,
    ];

    return {
      customParamsConfig,
      customParamSwitches,
      selectOptions,
      defaultOption,
    };
  }, [t]);

  const getSelectedOption = useCallback(
    (
      params: AnalyticsReportParams<WorkExperienceReportCustomParams>,
    ): SelectOptions => {
      const { incCompanyHistory, useOriginalStartDate } = params;

      const value: string = incCompanyHistory
        ? 'incCompanyHistory'
        : useOriginalStartDate
        ? 'useOriginalStartDate'
        : 'default';

      return (
        selectOptions.find((option) => option.value === value) ?? defaultOption
      );
    },
    [selectOptions, defaultOption],
  );

  return {
    customParamsConfig,
    customParamSwitches,
    selectOptions,
    getSelectedOption,
  };
};
